import * as React from 'react'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import ArrowRightIcon from '@wwt/shared/assets/icons/outline/arrow-right.svg'
import CheckIcon from '@wwt/shared/assets/icons/outline/check.svg'

import { cn } from '@/shared/lib/cn'

export const DropdownMenu = DropdownMenuPrimitive.Root

export const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger

export const DropdownMenuPortal = DropdownMenuPrimitive.Portal

export const DropdownMenuSub = DropdownMenuPrimitive.Sub

export const DropdownMenuSubTrigger = ({
	ref,
	className,
	inset,
	children,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubTrigger> & {
	inset?: boolean
}) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={cn(
			'bg-background text-foreground hover:bg-muted focus:bg-accent focus-visible:ring-primary data-[state=open]:bg-accent data-disabled:text-input flex min-h-12 items-center justify-between px-4 py-3 text-base font-normal outline-hidden transition-colors select-none ring-inset focus-visible:ring-1 focus-visible:outline-hidden data-disabled:pointer-events-none data-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
			inset && 'pl-8',
			className
		)}
		{...props}
	>
		{children}
		<ArrowRightIcon className="ml-auto" />
	</DropdownMenuPrimitive.SubTrigger>
)
DropdownMenuSubTrigger.displayName =
	DropdownMenuPrimitive.SubTrigger.displayName

export const DropdownMenuSubContent = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.SubContent>) => (
	<DropdownMenuPrimitive.SubContent
		ref={ref}
		className={cn(
			// TODO: fix z-index after refactoring entire app (to z-50)
			'bg-background text-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-1200 min-w-[13rem] overflow-hidden rounded-md border shadow-md [&>[role^=menuitem]:first-child]:rounded-t-md [&>[role^=menuitem]:last-child]:rounded-b-md',
			className
		)}
		{...props}
	/>
)
DropdownMenuSubContent.displayName =
	DropdownMenuPrimitive.SubContent.displayName

export const DropdownMenuContent = ({
	ref,
	className,
	sideOffset = 4,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Content>) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			className={cn(
				// TODO: fix z-index after refactoring entire app (to z-50)
				'bg-background text-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-1200 min-w-[13rem] overflow-hidden rounded-md border shadow-md [&>[role^=menuitem]:first-child]:rounded-t-md [&>[role^=menuitem]:last-child]:rounded-b-md',
				className
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
)
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName

export const DropdownMenuGroup = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Group>) => (
	<DropdownMenuPrimitive.Group
		ref={ref}
		className={cn(
			'[&:first-child>[role^=menuitem]:first-child]:rounded-t-md [&:last-child>[role^=menuitem]:last-child]:rounded-b-md',
			className
		)}
		{...props}
	/>
)
DropdownMenuGroup.displayName = DropdownMenuPrimitive.Group.displayName

export const DropdownMenuItem = ({
	ref,
	className,
	inset,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Item> & {
	inset?: boolean
}) => (
	<DropdownMenuPrimitive.Item
		ref={ref}
		className={cn(
			'bg-background text-foreground hover:bg-muted focus-visible:ring-primary data-disabled:text-input flex min-h-12 cursor-default items-center gap-4 px-4 py-3 text-base font-normal transition-colors select-none ring-inset hover:cursor-pointer focus-visible:ring-1 focus-visible:outline-hidden data-disabled:pointer-events-none data-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
			inset && 'pl-8',
			className
		)}
		{...props}
	/>
)
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName

export const DropdownMenuCheckboxItem = ({
	ref,
	className,
	children,
	checked,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.CheckboxItem>) => (
	<DropdownMenuPrimitive.CheckboxItem
		ref={ref}
		className={cn(
			'bg-background text-foreground hover:bg-muted focus-visible:ring-primary data-disabled:text-input flex min-h-12 cursor-default items-center gap-4 py-3 pr-4 pl-14 text-base font-normal transition-colors select-none ring-inset focus-visible:ring-1 focus-visible:outline-hidden data-disabled:pointer-events-none data-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
			className
		)}
		checked={checked}
		{...props}
	>
		<span className="absolute left-4 flex items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<CheckIcon />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.CheckboxItem>
)
DropdownMenuCheckboxItem.displayName =
	DropdownMenuPrimitive.CheckboxItem.displayName

export const DropdownMenuRadioGroup = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.RadioGroup>) => (
	<DropdownMenuPrimitive.RadioGroup
		ref={ref}
		className={cn(
			'[&:first-child>[role^=menuitem]:first-child]:rounded-t-md [&:last-child>[role^=menuitem]:last-child]:rounded-b-md',
			className
		)}
		{...props}
	/>
)
DropdownMenuRadioGroup.displayName =
	DropdownMenuPrimitive.RadioGroup.displayName

export const DropdownMenuRadioItem = ({
	ref,
	className,
	children,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.RadioItem>) => (
	<DropdownMenuPrimitive.RadioItem
		ref={ref}
		className={cn(
			'bg-background text-foreground hover:bg-muted focus-visible:ring-primary data-disabled:text-input flex min-h-12 cursor-default items-center justify-between py-3 pr-4 pl-14 text-base font-normal transition-colors select-none ring-inset focus-visible:ring-1 focus-visible:outline-hidden data-disabled:pointer-events-none data-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-6 [&_svg]:shrink-0',
			className
		)}
		{...props}
	>
		<span className="absolute left-4 flex items-center justify-center">
			<DropdownMenuPrimitive.ItemIndicator>
				<CheckIcon />
			</DropdownMenuPrimitive.ItemIndicator>
		</span>
		{children}
	</DropdownMenuPrimitive.RadioItem>
)
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName

export const DropdownMenuLabel = ({
	ref,
	className,
	inset,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Label> & {
	inset?: boolean
}) => (
	<DropdownMenuPrimitive.Label
		ref={ref}
		className={cn(
			'text-muted-foreground flex h-12 items-center gap-4 px-4 py-3 font-medium',
			inset && 'pl-8',
			className
		)}
		{...props}
	/>
)
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName

export const DropdownMenuSeparator = ({
	ref,
	className,
	...props
}: React.ComponentPropsWithRef<typeof DropdownMenuPrimitive.Separator>) => (
	<DropdownMenuPrimitive.Separator
		ref={ref}
		className={cn('bg-muted -mx-1 h-px', className)}
		{...props}
	/>
)
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName

export const DropdownMenuShortcut = ({
	className,
	...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
	return (
		<span
			className={cn(
				'text-muted-foreground ml-auto text-sm tracking-widest',
				className
			)}
			{...props}
		/>
	)
}
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'
